// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/ConfigurableCheckboxGroup/index.tsx

import { Box } from '@modusign/design-system';
import { Rect } from '@modusign/react-signing/types';
import { ReactNode, useCallback, useMemo } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { RoundedXButton } from '@app/components/Buttons';
import boxPositionToAbsolute from '@app/containers/FieldRenderers/libs/geometry/box/toAbsolute';
import CheckboxGroupBorder, {
  CHECKBOX_GROUP_BORDER_MARGIN_PX,
} from '@app/features/apply-react-signing/components/FieldRenderer/components/CheckboxGroupBorder';
import { createAbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsolutePoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import CheckboxGroupContextMenu from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/ConfigurableCheckboxGroup/CheckboxGroupContextMenu';
import { CheckboxGroupInputMode } from '@app/features/apply-react-signing/components/FieldRenderer/types';

interface ConfigurableCheckboxGroupProps {
  boundingBox: Rect;
  children: ReactNode[];
  isContextMenuOpen: boolean;
  participantOrder: number;
  participantName?: string;
  participantRole?: string;
  readOnly?: boolean;
  restrictModification?: boolean;
  shouldDrawBorder: boolean;
  viewport: { width: number; height: number };

  contextMenu: {
    required: boolean;
    mode: CheckboxGroupInputMode;
    count: number;
    dataLabel: string;
    isDataLabelDuplicated: boolean;
  };

  onDataLabelChange: (dataLabel: string) => void;
  onRequiredChange: (required: boolean) => void;
  onCheckBoxCountChange: (count: number) => void;
  onCheckBoxGroupModeChange: (mode: CheckboxGroupInputMode) => void;

  onDelete: () => void;
  onOutsideClick: () => void;
}

export default function ConfigurableCheckboxGroup({
  boundingBox,
  children,
  contextMenu,
  isContextMenuOpen,
  shouldDrawBorder,
  participantOrder,
  participantName,
  participantRole,
  readOnly,
  restrictModification,
  viewport,
  onDelete,
  onOutsideClick,
  onDataLabelChange,
  onRequiredChange,
  onCheckBoxCountChange,
  onCheckBoxGroupModeChange,
}: ConfigurableCheckboxGroupProps) {
  const { x, y, width, height } = boxPositionToAbsolute(boundingBox, viewport);

  const handleDelete = useCallback(() => {
    onDelete();
  }, [onDelete]);

  const handleCheckboxCountChange = (count: number) => {
    if (count === 1) {
      onCheckBoxGroupModeChange('RADIO');
    }
    onCheckBoxCountChange(count);
  };

  const dataLabelErrorMessage = useMemo(() => {
    if (!contextMenu.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (contextMenu.isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [contextMenu.dataLabel, contextMenu.isDataLabelDuplicated]);

  const handleDataLabelChange = useCallback(
    (dataLabel: string) => {
      onDataLabelChange(dataLabel);
    },
    [onDataLabelChange]
  );

  const handleRequiredChange = useCallback(
    (required: boolean) => {
      onRequiredChange(required);
    },
    [onRequiredChange]
  );

  const handleCheckboxGroupModeChange = useCallback(
    (mode: CheckboxGroupInputMode) => {
      if (contextMenu.count > 1) onCheckBoxGroupModeChange(mode);
    },
    [contextMenu.count, onCheckBoxGroupModeChange]
  );

  const displayedName =
    !participantName || participantName.length === 0 ? participantRole : participantName;

  return (
    <>
      <OutsideClickHandler onOutsideClick={onOutsideClick}>
        {!readOnly && !restrictModification && (
          <Box
            display={isContextMenuOpen ? 'block' : 'none'}
            position="absolute"
            style={{
              transform: `translate(calc(${x + width / 2}px - 50%), calc(${
                y - CHECKBOX_GROUP_BORDER_MARGIN_PX
              }px - 106%))`,
            }}
            zIndex={120}
          >
            <CheckboxGroupContextMenu
              checkboxCount={contextMenu.count}
              dataLabel={contextMenu.dataLabel}
              dataLabelErrorMessage={dataLabelErrorMessage}
              inputMode={contextMenu.mode}
              participantName={displayedName}
              participantOrder={participantOrder}
              required={contextMenu.required}
              restrictModification={restrictModification}
              onCheckboxCountSelect={handleCheckboxCountChange}
              onDataLabelChange={handleDataLabelChange}
              onInputModeChange={handleCheckboxGroupModeChange}
              onRequiredChange={handleRequiredChange}
            />
          </Box>
        )}
        {!readOnly && !restrictModification && isContextMenuOpen && (
          <Box
            position="absolute"
            style={{
              transform: `translate(${x + width + CHECKBOX_GROUP_BORDER_MARGIN_PX}px, ${
                y - CHECKBOX_GROUP_BORDER_MARGIN_PX / 2
              }px)`,
            }}
            zIndex={103}
          >
            <RoundedXButton aria-label="체크박스 삭제" onClick={handleDelete} />
          </Box>
        )}
      </OutsideClickHandler>

      <CheckboxGroupBorder
        checkboxFieldCount={contextMenu.count}
        mode={contextMenu.mode}
        participantOrder={participantOrder}
        point={createAbsolutePoint({ x, y })}
        required={contextMenu.required}
        size={createAbsoluteSize({ width, height })}
        visible={shouldDrawBorder || isContextMenuOpen}
      />

      <>{children}</>
    </>
  );
}
